/* eslint-disable no-bitwise */
const BASE64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

/** Encodes bytes as standard padded base64 without relying on browser or Node globals. */
export function bytesToBase64(bytes: Uint8Array): string {
  let encoded = '';
  for (let index = 0; index < bytes.length; index += 3) {
    const first = bytes[index] ?? 0;
    const second = bytes[index + 1] ?? 0;
    const third = bytes[index + 2] ?? 0;
    const packed = (first << 16) | (second << 8) | third;

    encoded += BASE64_ALPHABET[(packed >>> 18) & 0x3f];
    encoded += BASE64_ALPHABET[(packed >>> 12) & 0x3f];
    encoded += index + 1 < bytes.length ? BASE64_ALPHABET[(packed >>> 6) & 0x3f] : '=';
    encoded += index + 2 < bytes.length ? BASE64_ALPHABET[packed & 0x3f] : '=';
  }
  return encoded;
}

/** Decodes standard padded base64 without relying on browser or Node globals. */
export function base64ToBytes(encoded: string): Uint8Array {
  if (encoded.length === 0) { return new Uint8Array(); }

  if (encoded.length % 4 !== 0) {
    throw new Error(`Base64 length must be divisible by 4, got ${encoded.length}`);
  }

  if (!/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(encoded)) {
    throw new Error('Invalid base64 encoding');
  }

  const padding = encoded.endsWith('==') ? 2 : encoded.endsWith('=') ? 1 : 0;
  const bytes = new Uint8Array((encoded.length / 4) * 3 - padding);
  let outputIndex = 0;

  for (let index = 0; index < encoded.length; index += 4) {
    const isLastGroup = index + 4 === encoded.length;
    const first = decodeBase64Digit(encoded[index], false);
    const second = decodeBase64Digit(encoded[index + 1], false);
    const third = decodeBase64Digit(encoded[index + 2], isLastGroup);
    const fourth = decodeBase64Digit(encoded[index + 3], isLastGroup);
    const packed = (first << 18) | (second << 12) | (third << 6) | fourth;

    bytes[outputIndex++] = (packed >>> 16) & 0xff;
    if (outputIndex < bytes.length) { bytes[outputIndex++] = (packed >>> 8) & 0xff; }

    if (outputIndex < bytes.length) { bytes[outputIndex++] = packed & 0xff; }
  }

  return bytes;
}

function decodeBase64Digit(character: string | undefined, allowPadding: boolean): number {
  if (character === '=' && allowPadding) { return 0; }
  const value = character === undefined ? -1 : BASE64_ALPHABET.indexOf(character);
  if (value < 0) {
    throw new Error(`Invalid base64 character: ${character ?? '<missing>'}`);
  }
  return value;
}
